<template>
  <section class="channel-workspace">
    <!-- 顶栏操作 and 过滤框 -->
    <table-toolbar
      :search.sync="search"
      :show-search="true"
      :show-refresh="true"
      :show-column-settings="true"
      :columns="allConfigurableColumns"
      :visible-columns.sync="visibleColumns"
      :default-columns="defaultColumnsList"
      :fixed-columns="['# ID', '渠道名称', '操作']"
      :min-selected="4"
      @refresh="handleRefresh"
      :show-export="showExportBtn"
      @export="handleExport"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddChannel" class="btn-action">接入渠道</el-button>

        <el-input
          v-model.trim="searchForm.id"
          size="small"
          placeholder="渠道 ID"
          clearable
          class="filter-input-id"
          @input="handleIdInput"
          @keyup.enter.native="handleSearchChange"
          @clear="handleSearchChange"
        />

        <el-input
          v-model.trim="searchForm.name"
          size="small"
          placeholder="渠道名称"
          clearable
          class="filter-input-name"
          @keyup.enter.native="handleSearchChange"
          @clear="handleSearchChange"
        />

        <el-select v-model="searchForm.platformId" size="small" @change="handleSearchChange" placeholder="选择平台" clearable class="filter-select">
          <el-option value="">全部平台</el-option>
          <el-option v-for="item in platformData" :label="item.platformId + ' · ' + item.name" :value="item.platformId" :key="item.platformId"></el-option>
        </el-select>

        <el-select v-model="searchForm.status" size="small" @change="handleSearchChange" placeholder="选择状态" clearable class="filter-select">
          <el-option value="">全部状态</el-option>
          <el-option v-for="item in statusOption" :label="item.label" :value="item.value" :key="item.value"></el-option>
        </el-select>

        <cache-reload reload-type="channel" size="small" btn-label="重载NACOS-渠道" class="reload-wrapper" />
      </template>

    </table-toolbar>

    <!-- 表格主体 -->
    <table-page
      :loading="loading"
      :empty="tables.length === 0"
      :current-page.sync="currentPage"
      :page-size.sync="pageSize"
      :total="totalCount"
      :page-sizes="[10, 20, 30, 50, 100, 200]"
      :show-top-pagination="!!totalCount"
      :show-bottom-pagination="!!totalCount"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @retry="handleRefresh"
    >
      <el-table
        ref="channelTable"
        :data="tables"
        style="width: 100%"
        stripe
        class="premium-table"
      >
        <!-- ID列 (加前缀图标 #) -->
        <el-table-column prop="id" width="80">
          <template slot="header">
            <span class="premium-header-title"><span class="header-icon-hash">#</span> ID</span>
          </template>
          <template slot-scope="scope">
            <span class="cell-id">{{ scope.row.id }}</span>
          </template>
        </el-table-column>

        <!-- 渠道名称 (支持显示/隐藏控制) -->
        <el-table-column label="渠道名称" min-width="160">
          <template slot-scope="scope">
            <el-link type="primary" class="cell-channel-name" @click="handleUpdateChannel(scope.row)">
              {{ scope.row.name }}
            </el-link>
          </template>
        </el-table-column>

        <!-- 账号渠道ID -->
        <el-table-column v-if="visibleColumns.includes('accountChannelId')" prop="accountChannelId" label="账号渠道ID" width="125" sortable>
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.accountChannelId }}</span>
          </template>
        </el-table-column>

        <!-- 参数组ID -->
        <el-table-column v-if="visibleColumns.includes('argsGroupId')" prop="argsGroupId" label="参数组ID" width="115" sortable>
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.argsGroupId }}</span>
          </template>
        </el-table-column>

        <!-- 包类型/渠道类型 -->
        <el-table-column v-if="visibleColumns.includes('packageChannelType')" width="180">
          <template slot="header">
            <span class="premium-header-title">
              <span>包类型/渠道类型</span>
              <el-tooltip content="显示对应渠道的包端归属（如 Android/iOS）以及其接入的底层渠道分类" placement="top">
                <i class="el-icon-question" style="margin-left: 2px; color: #94a3b8; cursor: pointer;"></i>
              </el-tooltip>
            </span>
          </template>
          <template slot-scope="scope">
            <div class="package-channel-type-cell">
              <div class="package-type-line">
                <font-awesome-icon :icon="getPackageIcon(scope.row.packageType)" :style="{ color: getPackageColor(scope.row.packageType) }" class="platform-icon" />
                <span>{{ scope.row.packageType }}-{{ packageTypeFormatter(scope.row, null, scope.row.packageType) }}</span>
              </div>
              <div class="channel-type-line">
                {{ channelTypeFormatter(scope.row) }}
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- 平台 (调小宽度) -->
        <el-table-column v-if="visibleColumns.includes('platformId')" label="平台" width="95">
          <template slot-scope="scope">
            <span class="cell-platform">{{ platFormFormatter(scope.row, null, scope.row.platformId) }}</span>
          </template>
        </el-table-column>

        <!-- XsType -->
        <el-table-column v-if="visibleColumns.includes('xsType')" label="XsType" width="105" align="center">
          <template slot-scope="scope">
            <span :class="['status-dot-badge', scope.row.xsType === 1 ? 'online' : 'closed']">
              <span class="dot"></span>
              {{ scope.row.xsType === 1 ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <!-- 逻辑参数 -->
        <el-table-column v-if="visibleColumns.includes('logicArgs')" label="逻辑参数" min-width="140">
          <template slot-scope="scope">
            <el-link v-if="scope.row.edit" type="primary" class="cell-logic-args" @click="goToDetailTab(scope.row, 'args')">
              {{ scope.row.logicId }}-{{ scope.row.logicName }}
            </el-link>
            <el-link v-else type="info" class="cell-logic-args-none" @click="goToDetailTab(scope.row, 'args')">
              <i class="el-icon-edit-outline"></i> 去配置
            </el-link>
          </template>
        </el-table-column>

        <!-- 商品档位 -->
        <el-table-column v-if="visibleColumns.includes('products')" label="商品档位" width="105" align="center">
          <template slot-scope="scope">
            <el-link :underline="false" @click="goToDetailTab(scope.row, 'products')">
              <span :class="['products-badge-pill', scope.row.customized ? 'customized' : 'default']">
                {{ scope.row.customized ? '自定义' : '默认' }}
              </span>
            </el-link>
          </template>
        </el-table-column>

        <!-- 折扣 -->
        <el-table-column v-if="visibleColumns.includes('rate')" label="折扣" min-width="160">
          <template slot-scope="scope">
            <span class="cell-rate">{{ discountFormatter(scope.row) }}</span>
          </template>
        </el-table-column>

        <!-- 平台回调列 (移动至状态列前面) -->
        <el-table-column v-if="visibleColumns.includes('ptCallbackUrl')" label="平台回调" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.ptCallbackUrl" />
          </template>
        </el-table-column>

        <!-- 状态 -->
        <el-table-column v-if="visibleColumns.includes('status')" prop="status" label="状态" width="130">
          <template slot-scope="scope">
            <span :class="['status-dot-badge', getStatusClass(scope.row.status)]">
              <span class="dot"></span>
              {{ autoFormatter(scope.row, { property: 'status' }, scope.row.status) }}
            </span>
          </template>
        </el-table-column>

        <!-- 动态配置的自定义列：开关项与链接 -->
        <el-table-column v-if="visibleColumns.includes('minAge')" label="适龄" width="90" align="center">
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.minAge }}</span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('bizId')" label="bizId" width="90" align="center">
          <template slot-scope="scope">
            <span class="cell-text-gray">{{ scope.row.bizId }}</span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('realAuthOpen')" label="实名" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.realAuthOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.realAuthOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('juvenileOpen')" label="防沉迷" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.juvenileOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.juvenileOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('plutoOpen')" label="Pluto" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.plutoOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.plutoOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('ufwOpen')" label="UFW" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.ufwOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.ufwOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('phoneBindOpen')" label="手机绑定" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.phoneBindOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.phoneBindOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('loginOpen')" label="登录开关" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.loginOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.loginOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('payOpen')" label="支付" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.payOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.payOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('registerOpen')" label="注册开关" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.registerOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.registerOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('regLimitOpen')" label="注册限制" width="90" align="center">
          <template slot-scope="scope">
            <span :class="['status-simple-badge', scope.row.regLimitOpen ? 'active' : 'inactive']">
              <span class="dot"></span>
              {{ scope.row.regLimitOpen ? '开启' : '关闭' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('ptH5LoginUrl')" label="平台H5登录" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.ptH5LoginUrl" v-if="scope.row.packageType === 3" />
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('notifyUrl')" label="CP通知" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.notifyUrl" />
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('h5LoginUrl')" label="CPH5Url" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.h5LoginUrl" />
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('gameUrl')" label="CP微端Url" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.gameUrl" />
          </template>
        </el-table-column>

        <el-table-column v-if="visibleColumns.includes('packageName')" label="包名" width="130">
          <template slot-scope="scope">
            <ext-text-popover :content="scope.row.packageName" />
          </template>
        </el-table-column>

        <!-- 操作下拉菜单 (加前缀图标 ...) -->
        <el-table-column width="80" align="center">
          <template slot="header">
            <span class="premium-header-title">操作</span>
          </template>
          <template slot-scope="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="overview">
                  <i class="el-icon-view"></i> 链路概览
                </el-dropdown-item>
                <el-dropdown-item command="basic" divided>
                  <i class="el-icon-edit-outline"></i> 基础信息
                </el-dropdown-item>
                <el-dropdown-item command="products">
                  <i class="el-icon-coin"></i> 商品档位
                </el-dropdown-item>
                <el-dropdown-item command="args">
                  <i class="el-icon-setting"></i> 参数配置
                </el-dropdown-item>
                <el-dropdown-item command="official">
                  <font-awesome-icon icon="credit-card" /> 登录支付
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </table-page>

    <!-- 渠道配置 Drawer 抽屉 -->
    <el-drawer
      :visible.sync="drawerVisible"
      direction="rtl"
      size="50%"
      destroy-on-close
      :before-close="handleDrawerClose"
      custom-class="channel-detail-drawer"
      :with-header="false"
    >
      <channel-detail
        v-if="drawerVisible"
        :id="currentChannelId"
        :step="currentStepNum"
        :is-drawer="true"
        @close="handleDrawerClose"
        @save-success="handleSaveSuccess"
      />
    </el-drawer>
  </section>
</template>

<script>
/* eslint-disable vue/component-name-in-template-casing, vue/no-unused-vars */
import api from '@/api/channel'
import options from '@/common/options'
import f from '@/filter'
import CacheReload from '@/components/CacheReload/index.vue'
import ExtTextPopover from '@/components/ExtTextPopover/index.vue'
import * as XLSX from 'xlsx'
import ChannelDetail from './ChannelDialog.vue'
import TablePage from '@/components/TablePage/index.vue'
import TableToolbar from '@/components/TableToolbar/index.vue'

export default {
  name: 'index',
  components: { CacheReload, ExtTextPopover, ChannelDetail, TablePage, TableToolbar },
  data () {
    return {
      loading: false,
      pageSize: 20,
      currentPage: 1,
      totalCount: 0,
      tableData: [],
      argsData: new Map(),
      searchForm: {
        id: '',
        name: '',
        platformId: '',
        status: '',
        xsType: ''
      },
      search: '',
      logicData: new Map(),
      platformData: [],
      statusOption: options.statusOption,
      xsTypeOption: options.xsTypeOption,
      channelTypeList: [],
      areaMap: new Map(),
      showExportBtn: true,
      // 默认展示列包含 ptCallbackUrl (平台回调)，渠道名称为核心常驻字段
      visibleColumns: [
        'accountChannelId',
        'argsGroupId',
        'packageChannelType',
        'platformId',
        'xsType',
        'logicArgs',
        'products',
        'ptCallbackUrl',
        'status'
      ],
      // 抽屉相关变量
      drawerVisible: false,
      currentChannelId: null,
      currentStepNum: 0,
      defaultColumnsList: [
        'accountChannelId',
        'argsGroupId',
        'packageChannelType',
        'platformId',
        'xsType',
        'logicArgs',
        'products',
        'ptCallbackUrl',
        'status'
      ],
      allConfigurableColumns: [
        { value: 'accountChannelId', label: '账号渠道ID' },
        { value: 'argsGroupId', label: '参数组ID' },
        { value: 'packageChannelType', label: '包类型/渠道类型' },
        { value: 'platformId', label: '平台' },
        { value: 'xsType', label: 'XsType' },
        { value: 'logicArgs', label: '逻辑参数' },
        { value: 'products', label: '商品档位' },
        { value: 'rate', label: '折扣' },
        { value: 'ptCallbackUrl', label: '平台回调' },
        { value: 'status', label: '状态' },
        { value: 'minAge', label: '适龄' },
        { value: 'bizId', label: 'bizId' },
        { value: 'realAuthOpen', label: '实名' },
        { value: 'juvenileOpen', label: '防沉迷' },
        { value: 'plutoOpen', label: 'Pluto' },
        { value: 'ufwOpen', label: 'UFW' },
        { value: 'phoneBindOpen', label: '手机绑定' },
        { value: 'loginOpen', label: '登录开关' },
        { value: 'payOpen', label: '支付' },
        { value: 'registerOpen', label: '注册开关' },
        { value: 'regLimitOpen', label: '注册限制' },
        { value: 'ptH5LoginUrl', label: '平台H5登录' },
        { value: 'notifyUrl', label: 'CP通知' },
        { value: 'h5LoginUrl', label: 'CPH5Url' },
        { value: 'gameUrl', label: 'CP微端Url' },
        { value: 'packageName', label: '包名' }
      ]
    }
  },
  computed: {
    appId () {
      return this.$store.getters.appId
    },
    appInfo () {
      return this.$store.getters.appInfo
    },
    tables () {
      const search = this.search
      if (search) {
        return this.tableData.filter(data => {
          return Object.keys(data).some(key => {
            return String(data[key]).toLowerCase().indexOf(search.toLowerCase()) > -1
          })
        })
      }
      return this.tableData
    }
  },
  watch: {
    appId (val, oldVal) {
      if (val === oldVal) {
        return
      }
      this.fetchData(this.pageSize, this.currentPage)
    }
  },
  created () {
    this.$store.dispatch('GetChannelTypeList').then((res) => {
      this.channelTypeList = res
    })

    // 从 Dashboard 或全局搜索跳转带参数
    if (this.$route.query.search) {
      this.search = this.$route.query.search
    }
    if (this.$route.query.xsType !== undefined) {
      this.searchForm.xsType = this.$route.query.xsType
    }
    // 检查是否有新建指示
    if (this.$route.query.action === 'new') {
      this.$nextTick(() => {
        this.handleAddChannel()
      })
    }

    this.fetchData(this.pageSize, this.currentPage)
  },
  methods: {
    handleIdInput (val) {
      this.searchForm.id = String(val || '').replace(/\D/g, '')
    },
    fetchData (pageSize, currentPage) {
      this.loading = true
      const rawParams = Object.assign({}, this.searchForm, {
        size: pageSize,
        page: currentPage,
        appId: this.appId
      })
      const params = {}
      for (const key in rawParams) {
        const val = rawParams[key]
        if (val !== '' && val !== null && val !== undefined) {
          params[key] = key === 'id' ? Number(val) : val
        }
      }
      api.getAreas().then(res => {
        this.areaMap = this.mapData(res)
      })
      api.getPlatformData(this.appId).then(res => {
        this.platformData = res
      })
      api.getLogicData().then(res => {
        this.logicData = this.mapData(res)
      })
      api.getArgsList(this.appId).then(res => {
        this.argsData = this.mapData(res)
        api.getChannelDataByPage(params).then(res => {
          const list = (res && (res.data || res.list || (Array.isArray(res) ? res : []))) || []
          this.totalCount = (res && (res.total ?? res.totalCount)) || list.length
          for (const item of list) {
            item.edit = this.argsData.has(item.argsGroupId)
            if (item.edit) {
              item.customized = this.argsData.get(item.argsGroupId).customized
              const logicId = this.argsData.get(item.argsGroupId).logicId
              item.logicId = logicId
              item.logicName = this.logicData.get(logicId)?.name + (this.logicData.get(logicId)?.status === 1 ? '-废弃' : '')
            }
            item.ptH5LoginUrl = this.getPtH5LoginUrl(item)
            item.ptCallbackUrl = this.getPtCallback(item)
          }
          this.tableData = list
          this.loading = false
        }).catch(() => {
          this.loading = false
        })
      }).catch(() => {
        this.loading = false
      })
    },
    handleSearchChange () {
      this.currentPage = 1
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleResetSearch () {
      this.searchForm = {
        id: '',
        name: '',
        platformId: '',
        status: '',
        xsType: ''
      }
      this.currentPage = 1
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleRefresh () {
      this.fetchData(this.pageSize, this.currentPage)
    },
    handleExport () {
      if (!this.tables || this.tables.length === 0) {
        this.$message.warning('当前暂无可导出的数据')
        return
      }
      const exportData = this.tables.map(row => {
        return {
          渠道ID: row.id,
          渠道名称: row.name,
          平台: this.platFormFormatter(row, null, row.platformId),
          账号渠道ID: row.accountChannelId,
          参数组ID: row.argsGroupId,
          XsType: row.xsType === 1 ? '开启' : '关闭',
          '包类型/渠道类型': this.packageTypeFormatter(row, null, row.packageType),
          渠道类型: this.channelTypeFormatter(row),
          逻辑参数: row.edit ? `${row.logicId}-${row.logicName}` : '',
          折扣与方式: this.discountFormatter(row),
          状态: this.autoFormatter(row, { property: 'status' }, row.status),
          实名: row.realAuthOpen ? '开' : '关',
          防沉迷: row.juvenileOpen ? '开' : '关',
          Pluto: row.plutoOpen ? '开' : '关',
          UFW: row.ufwOpen ? '开' : '关',
          手机绑定: row.phoneBindOpen ? '开' : '关',
          登录开关: row.loginOpen ? '开' : '关',
          支付开关: row.payOpen ? '开' : '关',
          注册开关: row.registerOpen ? '开' : '关',
          注册限制: row.regLimitOpen ? '开' : '关',
          '平台-支付回调': row.ptCallbackUrl || '',
          '平台-H5登录Url': row.ptH5LoginUrl || '',
          'CP-NotifyUrl': row.notifyUrl || '',
          'CP-H5Url': row.h5LoginUrl || '',
          'CP-微端Url': row.gameUrl || '',
          包名: row.packageName || ''
        }
      })
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(exportData)
      XLSX.utils.book_append_sheet(workbook, worksheet, '渠道列表')
      XLSX.writeFile(workbook, `渠道列表-${new Date().getTime()}.xlsx`)
      this.$message.success('数据导出成功')
    },
    handleSizeChange (val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchData(val, this.currentPage)
    },
    handleCurrentChange (val) {
      this.currentPage = val
      this.fetchData(this.pageSize, val)
    },
    // 新增渠道 -> 打开抽屉
    handleAddChannel () {
      this.currentChannelId = null
      this.currentStepNum = 0
      this.drawerVisible = true
    },
    // 修改渠道 -> 打开抽屉并展示基础信息（第 1 步）
    handleUpdateChannel (rowData) {
      this.currentChannelId = rowData.id
      this.currentStepNum = 0
      this.drawerVisible = true
    },
    // 跳转至步骤向导指定步骤 -> 在抽屉中展示对应步骤
    goToDetailTab (rowData, tab) {
      let step = 0
      if (tab === 'urls') step = 1
      if (tab === 'products') step = 3
      if (tab === 'args') step = 4
      if (tab === 'advanced') step = 2
      if (tab === 'official') step = 5
      this.currentChannelId = rowData.id
      this.currentStepNum = step
      this.drawerVisible = true
    },
    handleDrawerClose () {
      this.drawerVisible = false
    },
    handleSaveSuccess () {
      this.drawerVisible = false
      this.fetchData(this.pageSize, this.currentPage)
    },
    // 快捷行操作下拉菜单分发
    handleActionCommand (command, row) {
      if (command === 'overview') {
        this.goToDetailTab(row, 'overview')
      } else if (command === 'basic') {
        this.goToDetailTab(row, 'basic')
      } else if (command === 'args') {
        this.goToDetailTab(row, 'args')
      } else if (command === 'products') {
        this.goToDetailTab(row, 'products')
      } else if (command === 'official') {
        this.goToDetailTab(row, 'official')
      }
    },
    // 辅助样式和格式化
    getPackageClass (type) {
      const map = {
        1: 'android', 2: 'ios', 3: 'h5', 4: 'mp', 5: 'harmony', 6: 'windows'
      }
      return map[type] || 'default'
    },
    getStatusClass (status) {
      if (status === 5) return 'online'
      if (status === 3 || status === 4) return 'testing'
      if (status === -1) return 'closed'
      return 'developing'
    },
    autoFormatter (row, column, cellValue) {
      return f.autoFormat(column.property, cellValue)
    },
    getArea (areaId) {
      const id = areaId || this.appInfo.areaId
      return this.areaMap.get(id) || {}
    },
    getPtCallback (row) {
      const logicId = row.logicId
      if (!logicId) return null
      const area = this.getArea(row.areaId)
      return area.callbackUrl + `/callback/c${logicId}/${row.id}`
    },
    getPtH5LoginUrl (row) {
      const logicId = row.logicId
      if (!logicId) return null
      const area = this.getArea(row.areaId)
      return area.gatewayUrl + `/h5/login/c${logicId}/${row.id}`
    },
    platFormFormatter (row, column, cellValue) {
      for (const item of this.platformData) {
        if (item.platformId === cellValue) {
          return item.platformId + '-' + item.name
        }
      }
      return cellValue
    },
    channelTypeFormatter (row) {
      if (!row.channelType) return row.channelType || ''
      const found = this.channelTypeList.find(t => t.id === row.channelType)
      return found ? `${found.id}-${found.name}` : row.channelType
    },
    packageTypeFormatter (row, column, cellValue) {
      return f.packageTypesFormat(cellValue)
    },
    rateListFormatter (row, column, cellValue) {
      return f.rateFormat(cellValue)
    },
    discountTypeFormatter (row, column, cellValue) {
      return f.discountTypeFormat(cellValue)
    },
    discountFormatter (row) {
      const rate = Number(row.rate)
      if (!rate) {
        return '-'
      }
      const rateLabel = String(this.rateListFormatter(row, null, row.rate)).replace(/^\d+-/, '')
      const discountTypeMap = {
        0: '渠道打折',
        1: '发行打折',
        2: '渠道打折'
      }
      return `${rateLabel}-${discountTypeMap[Number(row.discountType)] || this.discountTypeFormatter(row, null, row.discountType)}`
    },
    xsTypeFormatter (row, column, cellValue) {
      return f.xsTypeFormat(cellValue)
    },
    getPackageIcon (type) {
      const map = {
        1: ['fab', 'android'],
        2: ['fab', 'apple'],
        3: ['fas', 'globe'],
        4: ['fab', 'weixin'],
        5: ['fas', 'layer-group'],
        6: ['fab', 'windows']
      }
      return map[type] || ['fas', 'cube']
    },
    getPackageColor (type) {
      const map = {
        1: '#10B981', // 绿色
        2: '#64748B', // 灰色
        3: '#0ea5e9', // 蓝色
        4: '#07c160', // 微信绿
        5: '#f59e0b', // 鸿蒙橙
        6: '#ef4444' // 微软红
      }
      return map[type] || '#64748B'
    },
    mapData (value) {
      const data = new Map()
      for (const item of value) {
        data.set(item.id, item)
      }
      return data
    }
  }
}
</script>

<style scoped>
.channel-workspace {
  padding: 16px 24px;
}

.btn-action {
  flex-shrink: 0;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
}

.btn-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.filter-input-id {
  width: 110px;
  flex-shrink: 0;
}

.filter-input-name {
  width: 140px;
  flex-shrink: 0;
}

.filter-input-id :deep(.el-input__inner),
.filter-input-name :deep(.el-input__inner) {
  border-radius: 8px;
  background-color: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-main);
  font-weight: 500;
  height: 32px;
  line-height: 32px;
}

.filter-select {
  width: 150px;
  flex-shrink: 0;
}

.btn-search,
.btn-reset {
  height: 32px;
  border-radius: 8px !important;
  font-weight: 600 !important;
  padding: 0 14px !important;
}

.btn-reset {
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
}

.btn-reset:hover {
  background-color: var(--bg-hover) !important;
  border-color: var(--border-hover) !important;
  color: var(--text-main) !important;
}

.filter-select :deep(.el-input__inner) {
  border-radius: 8px;
  background-color: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-main);
  font-weight: 500;
  height: 32px;
  line-height: 32px;
  transition: all 0.2s ease;
}

.filter-select :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.filter-select :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.reload-wrapper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.reload-wrapper :deep(.el-button) {
  background-color: var(--bg-reload) !important;
  border-color: var(--border-reload) !important;
  color: var(--text-reload) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

.reload-wrapper :deep(.el-button:hover) {
  background-color: var(--bg-reload-hover) !important;
  color: var(--text-reload-hover) !important;
  border-color: var(--border-reload-hover) !important;
}

/* 按钮尺寸与刷新对齐统一，强制常规线条取消任何形式的加粗 */
.btn-setting-icon-large {
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0 !important;
  color: var(--text-muted) !important;
  background: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  border-radius: 8px !important;
}

.btn-setting-icon-large:hover {
  color: var(--text-main) !important;
  background: var(--bg-page) !important;
  border-color: var(--border-hover) !important;
}

/* 表格定制美化与字体优化 */
.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  font-size: 13px !important;
  color: var(--text-main) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right-header-wrapper) th,
.premium-table :deep(th.el-table__fixed-column) {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td {
  background-color: var(--bg-hover) !important;
}

.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

/* 带有美观符号的前置表头结构 (仅ID和操作包含前缀，颜色继承主文字) */
.premium-header-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: inherit;
}

.header-icon-hash {
  font-family: Menlo, Monaco, Consolas, monospace;
  font-size: 13.5px;
  font-weight: 900;
  color: inherit;
}

/* 单元格字体定制 */
.cell-id {
  font-weight: 600;
  color: var(--text-muted);
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-text-gray {
  color: var(--text-muted);
  font-weight: 500;
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace !important;
  font-size: 12.5px !important;
}

.cell-channel-name {
  font-weight: 700 !important;
  font-size: 13.5px !important;
  text-decoration: none !important;
}

.cell-platform {
  font-weight: 500;
  color: var(--text-main);
  font-size: 13px;
}

/* 徽章与胶囊 */
.package-channel-type-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.package-type-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--text-main);
  font-size: 13px;
}
.platform-icon {
  font-size: 14px;
  width: 14px;
  text-align: center;
}
.channel-type-line {
  font-size: 11.5px;
  color: var(--text-muted);
  font-weight: 500;
  padding-left: 20px;
}

.cell-logic-args {
  font-weight: 600 !important;
  font-size: 13px !important;
}
.cell-logic-args-none {
  font-size: 12.5px !important;
  font-weight: 500 !important;
}

.products-badge-pill {
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  display: inline-block;
}
.products-badge-pill.customized {
  background: rgba(168, 85, 247, 0.08);
  color: #7c3aed;
}
.products-badge-pill.default {
  background: rgba(148, 163, 184, 0.1);
  color: var(--text-muted);
}

.cell-rate {
  font-size: 13px;
  color: var(--text-main);
  font-weight: 500;
}

.status-dot-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.status-dot-badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-dot-badge.online {
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.08);
}
.status-dot-badge.online .dot {
  background-color: #10b981;
}

.status-dot-badge.testing {
  color: #d97706;
  background-color: rgba(245, 158, 11, 0.08);
}
.status-dot-badge.testing .dot {
  background-color: #f59e0b;
}

.status-dot-badge.developing {
  color: var(--primary-color);
  background-color: rgba(37, 99, 235, 0.08);
}
.status-dot-badge.developing .dot {
  background-color: var(--primary-color);
}

.status-dot-badge.closed {
  color: #dc2626;
  background-color: rgba(239, 68, 68, 0.08);
}
.status-dot-badge.closed .dot {
  background-color: #ef4444;
}

/* 操作列三点图标 */
.action-btn {
  font-size: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

/* 浮窗页 UI 高级美化定制 */
.column-settings-dialog :deep(.el-dialog) {
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.column-settings-dialog :deep(.el-dialog__header) {
  border-bottom: 1px solid var(--border-light);
  padding: 20px 24px 16px !important;
}

.column-settings-dialog :deep(.el-dialog__title) {
  font-weight: 700;
  color: var(--text-main);
  font-size: 16px;
}

.column-settings-dialog .dialog-body-desc {
  font-size: 13px;
  color: var(--text-muted);
  margin-top: -6px;
  margin-bottom: 20px;
  text-align: left;
  line-height: 1.6;
}

.column-settings-dialog .limit-warning {
  color: #f59e0b;
  font-weight: 600;
}

.column-settings-dialog .checkbox-container {
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 20px;
  background-color: var(--bg-page);
  max-height: 400px;
  overflow-y: auto;
}

.column-settings-dialog .dialog-column-group-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-muted);
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.column-settings-dialog .dialog-column-group-title:first-of-type {
  margin-top: 0;
}

.column-settings-dialog .dialog-column-group-title i {
  color: var(--primary-color);
  font-size: 14px;
}

.column-settings-dialog .fixed-columns-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 6px;
}

.column-settings-dialog .fixed-column-card {
  background: var(--bg-hover);
  border: 1px dashed var(--border-hover);
  color: var(--text-muted);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  user-select: none;
}

.column-settings-dialog .popover-divider {
  height: 1px;
  background: var(--border-color);
  margin: 18px 0;
}

.column-settings-dialog .dialog-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 0;
  width: 100%;
}

.column-settings-dialog .dialog-checkbox-group :deep(.el-checkbox) {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  padding: 8px 14px;
  border-radius: 8px;
  margin-right: 10px !important;
  margin-bottom: 6px !important;
  width: calc(33.33% - 10px);
  box-sizing: border-box;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.column-settings-dialog .dialog-checkbox-group :deep(.el-checkbox:hover) {
  border-color: var(--border-hover);
  background: var(--bg-page);
}

.column-settings-dialog .dialog-checkbox-group :deep(.el-checkbox.is-checked) {
  border-color: rgba(37, 99, 235, 0.4);
  background: rgba(37, 99, 235, 0.02);
}

.column-settings-dialog .dialog-checkbox-group :deep(.el-checkbox__label) {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-main);
}

.column-settings-dialog .dialog-checkbox-group :deep(.el-checkbox__inner) {
  border-radius: 4px;
}

.column-settings-dialog .btn-dialog-action {
  border-radius: 8px !important;
  padding: 8px 18px !important;
  font-weight: 600 !important;
}

.column-settings-dialog :deep(.el-dialog__footer) {
  border-top: 1px solid var(--border-light);
  padding: 16px 24px 20px !important;
  text-align: inherit !important;
}

.column-settings-dialog .dialog-footer {
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
}
</style>
